import { create } from 'zustand';
import { chatbotApi } from '../api/chatbot.api';
import { ChatbotFeedbackResponse, ChatbotSatisfaction } from '../models/chatbot.entity';

export const FEEDBACK_ERP_ERROR_NAME = 'FeedbackErpError';

export interface Message {
  conversationId: string;
  text: string;
  sender: 'user' | 'bot';
}

interface MessageStore {
  actualConversationId: string;
  messages: Message[];
  initSession: (text: string, userId: string) => Promise<void>;
  sendMessage: (text: string, userId: string) => Promise<void>;
  submitFeedback: (userId: string, satisfaction: ChatbotSatisfaction) => Promise<ChatbotFeedbackResponse>;
  deleteSession: () => void;
}

export const useMessageStore = create<MessageStore>((set, get) => ({
  messages: [],
  actualConversationId: '',
  initSession: async (text: string, userId: string) => {
    const userMessage: Message = {
      conversationId: '',
      text,
      sender: 'user',
    };

    set((state) => ({ messages: [...state.messages, userMessage] }));

    const chatbotResponse = await chatbotApi.initiateChatbot(userId, text);
    if (chatbotResponse.error_code && chatbotResponse.error_code !== 'N/A') {
      throw new Error(`Failed to initialize conversation: ${chatbotResponse.error_code}`);
    }
    const botMessage: Message = {
      conversationId: chatbotResponse.conversation_id,
      text: chatbotResponse.response,
      sender: 'bot',
    };

    set((state) => ({
      actualConversationId: chatbotResponse.conversation_id,
      messages: [...state.messages, botMessage],
    }));
  },
  sendMessage: async (text: string, userId: string) => {
    const userMessage: Message = {
      conversationId: get().actualConversationId,
      text,
      sender: 'user',
    };

    set((state) => ({ messages: [...state.messages, userMessage] }));

    const chatbotResponse = await chatbotApi.responseChatbot(userId, text, get().actualConversationId);

    if (get().actualConversationId !== chatbotResponse.conversation_id) {
      throw new Error('Failed to send message: different conversation ID');
    }
    const botMessage: Message = {
      conversationId: chatbotResponse.conversation_id,
      text: chatbotResponse.response,
      sender: 'bot',
    };
    set((state) => ({
      messages: [...state.messages, botMessage],
    }));
  },
  submitFeedback: async (userId: string, satisfaction: ChatbotSatisfaction) => {
    const conversationId = get().actualConversationId;
    if (!conversationId) {
      throw new Error('Cannot submit feedback without an active conversation');
    }

    const response = await chatbotApi.feedbackChatbot(userId, satisfaction, conversationId);
    if (response.status !== 'feedback_processed' || response.conversation_id !== conversationId) {
      throw new Error('Failed to process feedback');
    }
    if (satisfaction === 'negative' && response.bc_save_status !== 'success') {
      const error = new Error('Failed to save feedback in ERP');
      error.name = FEEDBACK_ERP_ERROR_NAME;
      throw error;
    }

    return response;
  },
  deleteSession: () => {
    set({ messages: [], actualConversationId: '' });
  },
}));

export default useMessageStore;
